import type { TextComponentsObj } from "@brexhq/shared/components/Typography/Text";
import type { SemanticTextTag } from "@brexhq/shared/types/Typography";

export type TextStyle =
  | "Display XL"
  | "Display L"
  | "EVP Display L"
  | "Display M"
  | "EVP Display M"
  | "Display S"
  | "Title L"
  | "Title M"
  | "Title S"
  | "Subtitle L"
  | "Subtitle M"
  | "Subtitle S"
  | "EVP Subtitle S"
  | "Subtitle S Regular"
  | "Body L"
  | "Body M"
  | "Body S"
  | "Legal"
  | "Headline L"
  | "Headline M"
  | "Headline S"
  | "Flecha L"
  | "Flecha M"
  | "Flecha S"
  | "Flecha XS"
  | "Space Mono L"
  | "Space Mono M"
  | "Space Mono S"
  | "Space Mono XS"
  | "Space Mono Body S";

export type SanityTextPickerOptions = {
  [k in TextStyle]: {
    as: SemanticTextTag;
    components: TextComponentsObj;
  };
};

export const ThemeTextComponents: SanityTextPickerOptions = {
  "Display XL": {
    as: "h2",
    components: {
      LRG: "DisplayXL",
      MED: "DisplayXL",
      SML: "DisplayXL",
    },
  },
  "Display L": {
    as: "h2",
    components: {
      LRG: "DisplayL",
      MED: "DisplayLTablet",
      SML: "DisplayMobile",
    },
  },
  "EVP Display L": {
    as: "h2",
    components: {
      LRG: "EVPDisplayL",
      MED: "EVPDisplayL",
      SML: "EVPDisplayLMobile",
    },
  },
  "Display M": {
    as: "h2",
    components: {
      LRG: "DisplayM",
      MED: "DisplayMTablet",
      SML: "DisplayMobile",
    },
  },
  "EVP Display M": {
    as: "h2",
    components: {
      LRG: "EVPDisplayM",
      MED: "EVPDisplayM",
      SML: "EVPDisplayMMobile",
    },
  },
  "Display S": {
    as: "h2",
    components: {
      LRG: "DisplayS",
      MED: "DisplaySTablet",
      SML: "DisplayMobile",
    },
  },
  "Title L": {
    as: "h3",
    components: {
      LRG: "TitleL",
      MED: "TitleM",
      SML: "TitleS",
    },
  },
  "Title M": {
    as: "h3",
    components: {
      LRG: "TitleM",
      MED: "TitleS",
      SML: "SubTitleL",
    },
  },
  "Title S": {
    as: "h3",
    components: {
      LRG: "TitleS",
      MED: "SubTitleL",
      SML: "SubTitleM",
    },
  },
  "Subtitle L": {
    as: "h4",
    components: {
      LRG: "SubTitleL",
      MED: "SubTitleL",
      SML: "SubTitleL",
    },
  },
  "Subtitle M": {
    as: "h4",
    components: {
      LRG: "SubTitleM",
      MED: "SubTitleM",
      SML: "SubTitleM",
    },
  },
  "Subtitle S": {
    as: "h4",
    components: {
      LRG: "SubTitleS",
      MED: "SubTitleS",
      SML: "SubTitleS",
    },
  },
  "EVP Subtitle S": {
    as: "h4",
    components: {
      LRG: "EVPSubTitleS",
      MED: "EVPSubTitleS",
      SML: "EVPSubTitleS",
    },
  },
  "Subtitle S Regular": {
    as: "h4",
    components: {
      LRG: "SubTitleSRegular",
      MED: "BodyL",
      SML: "BodyL",
    },
  },
  "Headline L": {
    as: "h2",
    components: {
      LRG: "Headline1",
      MED: "Headline2",
      SML: "Headline3",
    },
  },
  "Headline M": {
    as: "h2",
    components: {
      LRG: "Headline2",
      MED: "Headline3",
      SML: "Headline4",
    },
  },
  "Headline S": {
    as: "h2",
    components: {
      LRG: "Headline3",
      MED: "Headline4",
      SML: "Headline4",
    },
  },
  "Body L": {
    as: "p",
    components: {
      LRG: "BodyL",
      MED: "BodyL",
      SML: "BodyL",
    },
  },
  "Body M": {
    as: "p",
    components: {
      LRG: "BodyM",
      MED: "BodyM",
      SML: "BodyM",
    },
  },
  "Body S": {
    as: "p",
    components: {
      LRG: "BodyS",
      MED: "BodyS",
      SML: "BodyS",
    },
  },
  Legal: {
    as: "div" as SemanticTextTag,
    components: {
      LRG: "Legal",
      MED: "Legal",
      SML: "Legal",
    },
  },
  "Flecha L": {
    as: "h2",
    components: {
      LRG: "FlechaL",
      MED: "FlechaM",
      SML: "FlechaS",
    },
  },
  "Flecha M": {
    as: "h2",
    components: {
      LRG: "FlechaM",
      MED: "FlechaS",
      SML: "FlechaS",
    },
  },
  "Flecha S": {
    as: "h4",
    components: {
      LRG: "FlechaS",
      MED: "FlechaXS",
      SML: "FlechaXS",
    },
  },
  "Flecha XS": {
    as: "h4",
    components: {
      LRG: "FlechaXS",
      MED: "FlechaXS",
      SML: "FlechaXS",
    },
  },
  "Space Mono L": {
    as: "p",
    components: {
      LRG: "SpaceMonoL",
      MED: "SpaceMonoM",
      SML: "SpaceMonoS",
    },
  },
  "Space Mono M": {
    as: "p",
    components: {
      LRG: "SpaceMonoM",
      MED: "SpaceMonoS",
      SML: "SpaceMonoS",
    },
  },
  "Space Mono S": {
    as: "p",
    components: {
      LRG: "SpaceMonoS",
      MED: "SpaceMonoS",
      SML: "SpaceMonoXS",
    },
  },
  "Space Mono XS": {
    as: "p",
    components: {
      LRG: "SpaceMonoXS",
      MED: "SpaceMonoXS",
      SML: "SpaceMonoXS",
    },
  },
  "Space Mono Body S": {
    as: "p",
    components: {
      LRG: "SpaceMonoBodyS",
      MED: "SpaceMonoBodyS",
      SML: "SpaceMonoBodyS",
    },
  },
};
